import { formatPercent } from "@angular/common";
import { ChangeDetectionStrategy, Component, computed, inject, input, output, signal } from "@angular/core";
import { AccountSelectionService, AuthService, UserSelectionService } from "@front/m19-services";
import { DateAggregation } from "@front/m19-utils";
import { ChartDataset, Point } from "chart.js";
import { SovBrandDateType } from "../../share-of-voice/share-of-voice.component";
import { TranslocoDirective } from "@jsverse/transloco";
import { SpinnerComponent } from "@front/m19-ui";
import {
  ChartConfig,
  IChartComponent,
  IDateAggreationSwitchComponent,
  ISwitchButtonComponent,
  SwitchButtonType,
} from "@front/m19-ui";
import { toSignal } from "@angular/core/rxjs-interop";
import { DateTime } from "luxon";

@Component({
  selector: "app-sov-main-chart",
  templateUrl: "./sov-main-chart.component.html",
  styleUrls: ["./sov-main-chart.component.scss"],
  imports: [
    TranslocoDirective,
    SpinnerComponent,
    ISwitchButtonComponent,
    IDateAggreationSwitchComponent,
    IChartComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SovMainChartComponent {
  private readonly authService = inject(AuthService);
  private readonly userSelectionService = inject(UserSelectionService);
  private readonly accountSelectionService = inject(AccountSelectionService);

  readonly SwitchButtonType = SwitchButtonType;

  isBarChart = signal(false);
  locale = toSignal(this.authService.userLocale$, { requireSync: true });
  accountMarketplace = toSignal(this.accountSelectionService.singleAccountMarketplaceSelection$, { requireSync: true });
  selectedDates = toSignal(this.userSelectionService.selectedDateRange$, { requireSync: true });
  dateAggregation = signal(DateAggregation.daily);

  hoveredBrand = input<string>();
  brandColors = input<Map<string, string>>();
  brandSelection = input.required<string[]>();
  sovData = input.required<SovBrandDateType>();
  loading = input<boolean>();

  emitSnapshotDate = output<Date>();

  selectedBrandsData = computed(() => {
    const data = new Map<string, Map<number, number>>();

    for (const [brand, dateMap] of this.sovData()) {
      if (!this.brandSelection().includes(brand)) continue;
      data.set(brand, dateMap);
    }

    return data;
  });

  readonly chartData = computed<Point[][]>(() => {
    const startDateTimestamp = this.selectedDates().start.startOf("day").toMillis();
    const endDateTimestamp = this.selectedDates().end.toMillis();
    const data: Point[][] = [];
    for (const [_, dateMap] of this.selectedBrandsData()) {
      const points: Point[] = [];
      for (const [date, sov] of dateMap) {
        if (date < startDateTimestamp || date > endDateTimestamp) continue;
        points.push({ x: date, y: sov });
      }
      data.push(points);
    }
    return data;
  });

  readonly lineChartConfig = computed<ChartConfig<Point[], "timeSeriesRanking">>(() => {
    return {
      type: "timeSeriesRanking",
      aspectRatio: 3,
      marketplace: this.accountMarketplace().marketplace,
      dateRange: this.selectedDates(),
      dateAggregation: this.dateAggregation(),
      title: "",
      legends: Array.from(this.selectedBrandsData().keys()),
      aggFunctions: [],
      metrics: [],
      colorsByLabel: this.brandColors(),
      displayLegend: false,
      labelFormatter: (v: string | number) => {
        return formatPercent(+v / 100, this.locale(), "1.0");
      },
      datasetOutlineIdentifier: (dataset: ChartDataset<"line" | "bar">) => dataset.label,
      isBarChart: this.isBarChart(),
      onClick: (clickedValue) => {
        const fullDate = DateTime.fromMillis(clickedValue).startOf("day").setZone("utc", { keepLocalTime: true });
        this.emitSnapshotDate.emit(fullDate.toJSDate());
      },
      tooltipFooter: () => "\nClick to set snapshot date",
    };
  });

  changeChartType(isBarChart?: boolean) {
    if (isBarChart !== undefined) this.isBarChart.set(isBarChart);
    else this.isBarChart.update((v) => !v);
  }

  selectAggregation(aggregation: DateAggregation): void {
    this.dateAggregation.set(aggregation);
  }
}
